import type { Option } from '@taki/ui';

/**
 * JEU DE DONNEES DE DEMONSTRATION DES VUES NATIVES, EN MEMOIRE.
 *
 * POURQUOI C'EST LEGITIME ICI, ET SEULEMENT ICI : `ui-lab` est le banc d'essai
 * du design system (app de demonstration, sans donnees ni ecriture reelle, cf.
 * sa fiche `taki.app.json` et son BRIEF.md). Une app METIER, elle, n'a jamais
 * le droit a des donnees fictives en dur : elle passe par une FAMILLE du socle
 * via `@taki/sdk` (docs/PLAYBOOK-APP.md, section 8). Ce fichier ne parle donc a aucun
 * SDK et n'ecrit dans aucune base : il simule un chargeur, rien de plus.
 *
 * CE JEU PORTE TOUS LES ROLES DU CONTRAT, c'est sa seule raison d'etre : sans
 * `groupKey` le kanban serait grise, sans `dateStart` le calendrier aussi, et
 * la vitrine ne prouverait rien.
 *
 *   subject   -> title        | place  -> subtitle   | owner  -> avatar
 *   status    -> status ET groupKey    | team   -> lane
 *   startAt   -> dateStart    | endAt  -> dateEnd
 *   duration / cost / owner   -> colonnes du tableau
 *   progress  -> role LIBRE `avancement`, invente par la vue custom de l'app
 *
 * L'enregistrement archive du bas prouve une regle du core qu'aucune vue n'a a
 * connaitre : `useScreen` retire les archives AVANT la projection, donc aucune
 * des six vues ne le voit jamais.
 */

/** Un enregistrement de demonstration. Aucune famille du socle : c'est un decor. */
export interface DemoIntervention {
  id: string;
  /** Role `title`. */
  subject: string;
  /** Role `subtitle`. Volontairement vide sur une ligne : le texte `empty` du champ prend le relais. */
  place: string;
  /** Nom de la personne assignee : le role `avatar` en tire les initiales. */
  owner: string;
  /** Role `status` (liste, tableau) ET role `groupKey` (colonnes du kanban). */
  status: string;
  /** Role `lane` : les puces de couloir du calendrier. */
  team: string;
  /** Role `dateStart`. */
  startAt: Date;
  /** Role `dateEnd` : seules les interventions qui durent plusieurs jours en ont un. */
  endAt?: Date;
  /** Colonne du tableau (heures), agregat `avg`. */
  duration: number;
  /** Colonne du tableau (EUR), agregat `sum`. */
  cost: number;
  /** Role LIBRE `avancement` (0 a 100), lu par la seule vue custom `ui-lab.jauge`. */
  progress: number;
  /** Suppression logique du socle : le core exclut ces lignes de toutes les vues. */
  archivedAt: Date | null;
}

/* ------------------------------------------------------------------ */
/* Les catalogues (ce que `options()` rend au runtime)                 */
/* ------------------------------------------------------------------ */

/**
 * Catalogue des etats. Dans une vraie app il viendrait des reglages du tenant
 * (recommandation Q2 de la specification) ; ici il est fige, c'est un decor.
 * Le `tone` est le SEUL vocabulaire de couleur partage : les mots appartiennent
 * au metier, les couleurs a la plateforme.
 */
export const STATUS_OPTIONS: Option[] = [
  { value: 'a-faire', label: 'A faire', tone: 'neutral' },
  { value: 'en-cours', label: 'En cours', tone: 'accent' },
  { value: 'en-attente', label: 'En attente', tone: 'danger' },
  { value: 'termine', label: 'Termine', tone: 'success' },
];

/** Catalogue des equipes : les couloirs du calendrier. */
export const TEAM_OPTIONS: Option[] = [
  { value: 'atelier', label: 'Atelier', tone: 'neutral' },
  { value: 'terrain', label: 'Terrain', tone: 'accent' },
  { value: 'bureau', label: 'Bureau', tone: 'neutral' },
];

/** Libelle d'une valeur de catalogue, pour les colonnes du tableau (tri sur le mot affiche). */
export function labelOf(options: readonly Option[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

/**
 * Initiales pour le role `avatar` : c'est l'ECRAN qui les fournit, jamais la
 * vue (le contrat dit "une URL ou des initiales, selon ce que l'ecran fournit").
 */
export function ownerInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== '');
  const [first, second] = words;
  if (first == null) return '';
  if (second != null) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  return first.slice(0, 2).toUpperCase();
}

/* ------------------------------------------------------------------ */
/* Les enregistrements                                                 */
/* ------------------------------------------------------------------ */

/**
 * Dates RELATIVES au jour d'ouverture : le calendrier doit etre nourri quel que
 * soit le jour ou le fondateur ouvre la vitrine. Heure fixe, pas de minute
 * fantaisiste : c'est une demonstration, pas une simulation.
 */
const TODAY = new Date();
function day(offset: number, hour = 9): Date {
  return new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() + offset, hour, 0, 0, 0);
}

/**
 * L'unique source de verite du decor. MUTABLE : le depot d'une carte de kanban
 * ecrit vraiment ici (via `setDemoStatus`), pour que le circuit complet
 * "vue -> onPatchRecord -> optimisme du core -> set() de l'ecran" soit
 * demontrable de bout en bout, sans base de donnees.
 */
const STORE: DemoIntervention[] = [
  { id: 'int-01', subject: "Remplacer le filtre a air", place: 'Atelier Nord', owner: 'Ada Lovelace',
    status: 'en-cours', team: 'atelier', startAt: day(0), duration: 3, cost: 180, progress: 60, archivedAt: null },
  { id: 'int-02', subject: 'Reviser la pompe 4', place: 'Hall B', owner: 'Bruno Costa',
    status: 'a-faire', team: 'atelier', startAt: day(2), duration: 5, cost: 320, progress: 10, archivedAt: null },
  { id: 'int-03', subject: 'Controle annuel des extincteurs', place: 'Site Sud', owner: 'Chiara Rossi',
    status: 'a-faire', team: 'terrain', startAt: day(4), endAt: day(5, 17), duration: 8, cost: 540, progress: 0, archivedAt: null },
  { id: 'int-04', subject: 'Diagnostic electrique', place: '', owner: 'Diego Alvarez',
    status: 'en-attente', team: 'terrain', startAt: day(-1), duration: 2, cost: 140, progress: 25, archivedAt: null },
  { id: 'int-05', subject: 'Nettoyage des conduits', place: 'Atelier Nord', owner: 'Elsa Meyer',
    status: 'termine', team: 'atelier', startAt: day(-4), duration: 4, cost: 260, progress: 100, archivedAt: null },
  { id: 'int-06', subject: 'Remise en service du compresseur', place: 'Hall B', owner: 'Farid Benali',
    status: 'en-cours', team: 'atelier', startAt: day(1), duration: 6, cost: 410, progress: 45, archivedAt: null },
  { id: 'int-07', subject: 'Inventaire des pieces detachees', place: 'Magasin', owner: 'Gaia Bruni',
    status: 'a-faire', team: 'bureau', startAt: day(6), duration: 3, cost: 90, progress: 0, archivedAt: null },
  { id: 'int-08', subject: 'Reparation du portail', place: 'Site Sud', owner: 'Hugo Lemaire',
    status: 'en-attente', team: 'terrain', startAt: day(3), duration: 2, cost: 150, progress: 15, archivedAt: null },
  { id: 'int-09', subject: 'Mise a jour du plan de maintenance', place: 'Bureau', owner: 'Ines Duarte',
    status: 'en-cours', team: 'bureau', startAt: day(-2), endAt: day(2, 17), duration: 10, cost: 0, progress: 70, archivedAt: null },
  { id: 'int-10', subject: 'Changer les roulements', place: 'Atelier Nord', owner: 'Jonas Klein',
    status: 'termine', team: 'atelier', startAt: day(-6), duration: 7, cost: 480, progress: 100, archivedAt: null },
  { id: 'int-11', subject: 'Verifier les niveaux', place: 'Hall B', owner: 'Kenza Idrissi',
    status: 'a-faire', team: 'atelier', startAt: day(8), duration: 1, cost: 40, progress: 0, archivedAt: null },
  { id: 'int-12', subject: 'Audit securite du quai', place: 'Site Sud', owner: 'Louis Perret',
    status: 'a-faire', team: 'terrain', startAt: day(11), endAt: day(12, 17), duration: 9, cost: 620, progress: 0, archivedAt: null },
  { id: 'int-13', subject: 'Remplacer les joints', place: 'Atelier Nord', owner: 'Maya Silva',
    status: 'en-cours', team: 'atelier', startAt: day(0, 14), duration: 2, cost: 95, progress: 30, archivedAt: null },
  { id: 'int-14', subject: "Former l'equipe au nouveau poste", place: 'Bureau', owner: 'Nils Fischer',
    status: 'a-faire', team: 'bureau', startAt: day(5), duration: 4, cost: 0, progress: 0, archivedAt: null },
  // Archive : le core l'ecarte avant la projection, aucune vue ne le voit.
  { id: 'int-15', subject: 'Ancien contrat de sous-traitance', place: 'Bureau', owner: 'Ada Lovelace',
    status: 'termine', team: 'bureau', startAt: day(-40), duration: 12, cost: 1200, progress: 100, archivedAt: day(-30) },
];

/* ------------------------------------------------------------------ */
/* Le faux chargeur                                                    */
/* ------------------------------------------------------------------ */

/**
 * Ce que `useScreen({ load })` appelle. Rend des COPIES : le decor ne se laisse
 * pas modifier par accident depuis l'ecran, seule `setDemoStatus` ecrit.
 */
export function listDemoInterventions(): Promise<DemoIntervention[]> {
  return Promise.resolve(STORE.map((item) => ({ ...item })));
}

/**
 * Ce que `useScreen({ get })` appelle a froid (reprise de session sur une fiche
 * dont la collection n'est pas encore chargee).
 */
export function getDemoIntervention(id: string): Promise<DemoIntervention | null> {
  const found = STORE.find((item) => item.id === id);
  return Promise.resolve(found == null ? null : { ...found });
}

/**
 * Le `set()` du role `groupKey` : ce que le depot d'une carte de kanban ecrit.
 * Une valeur hors catalogue est refusee, comme le ferait un schema Zod cote SDK.
 */
export function setDemoStatus(id: string, value: string): Promise<void> {
  const found = STORE.find((item) => item.id === id);
  if (found == null) return Promise.reject(new Error(`Intervention ${id} introuvable.`));
  if (!STATUS_OPTIONS.some((option) => option.value === value)) {
    return Promise.reject(new Error(`Etat inconnu : ${value}.`));
  }
  found.status = value;
  return Promise.resolve();
}
